/**
 * Who in this organization may use one MCP server.
 *
 * Three layers, all enforced by mcp-gateway on every call: the organization has
 * the server turned on (platform admin), the person is a member of it, and a
 * rule — or the server's default — allows them. builder-backend is the single
 * authority; the gateway polls the resolved result, so a save here takes effect
 * within its poll interval (~60s), including for people who are already
 * connected.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Input, Select, Space, Spin, Switch, Table, Tag, Typography, message } from 'antd';
import { DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import {
  fetchMcpTenantAccess,
  saveMcpAccessRules,
  saveMcpIdentityProvider,
  updateMcpTenantServer,
} from '../../services/api.service';
import { useUser } from '../../contexts/UserContext';
import type {
  McpAccessRule,
  McpDefaultAccess,
  McpPrincipalType,
  McpTenantAccess,
  McpTenantServerAccess,
} from '../../types/mcp';

const { Text, Paragraph } = Typography;

interface Props {
  serverId: string;
  tenant: string;
  /** Called after a change that alters what the server catalog shows. */
  onChanged?: () => void;
}

type Verdict = 'allow' | 'deny';
const VERDICT_OPTIONS: Array<{ value: Verdict; label: string }> = [
  { value: 'allow', label: 'Permitir' },
  { value: 'deny', label: 'Denegar' },
];

const ROLE_OPTIONS = ['admin', 'editor', 'viewer'].map((role) => ({ value: role, label: role }));

const McpAccessPanel: React.FC<Props> = ({ serverId, tenant, onChanged }) => {
  const { isSuperadmin } = useUser();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [access, setAccess] = useState<McpTenantAccess | null>(null);
  const [rules, setRules] = useState<McpAccessRule[]>([]);
  const [domains, setDomains] = useState<string[]>([]);
  const [autoProvision, setAutoProvision] = useState(false);

  const [newType, setNewType] = useState<McpPrincipalType>('email');
  const [newPrincipal, setNewPrincipal] = useState('');
  const [newAllowed, setNewAllowed] = useState(true);

  const server: McpTenantServerAccess | undefined = access?.servers.find((s) => s.serverId === serverId);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMcpTenantAccess(tenant);
      setAccess(data);
      setRules(data.servers.find((s) => s.serverId === serverId)?.rules ?? []);
      setDomains(data.identityProvider?.allowedDomains ?? []);
      setAutoProvision(data.identityProvider?.autoProvision ?? false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error cargando el acceso MCP');
    } finally {
      setLoading(false);
    }
  }, [serverId, tenant]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (action: () => Promise<void>, success: string): Promise<void> => {
    setSaving(true);
    try {
      await action();
      message.success(success);
      await load();
      onChanged?.();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const addRule = (): void => {
    const principal = newPrincipal.trim().toLowerCase();
    if (!principal) return;
    if (rules.some((rule) => rule.principalType === newType && rule.principal === principal)) {
      message.warning('Ya existe una regla para ese destinatario');
      return;
    }
    setRules([...rules, { principalType: newType, principal, allowed: newAllowed }]);
    setNewPrincipal('');
  };

  if (loading) {
    return (
      <Card size="small" title="Acceso de la organización" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><Spin /></div>
      </Card>
    );
  }

  if (error || !access || !server) {
    return (
      <Card size="small" title="Acceso de la organización" style={{ marginBottom: 16 }}>
        <Alert type="error" showIcon message={error ?? 'Sin datos de acceso'} />
      </Card>
    );
  }

  const dirty = JSON.stringify(rules) !== JSON.stringify(server.rules);

  return (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      title={
        <Space>
          <span>Acceso de la organización</span>
          <Tag color="blue">{tenant}</Tag>
          <Tag color={server.enabled ? 'green' : 'default'}>{server.enabled ? 'Activado' : 'Desactivado'}</Tag>
        </Space>
      }
      extra={
        <Text type="secondary">
          {server.grantedCount} de {access.memberCount} miembros con acceso
        </Text>
      }
    >
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        Para usar este servidor una persona debe ser miembro de la organización y estar permitida
        por una regla o por la regla por defecto. Un cambio se aplica en ~1 minuto, también a quien
        ya esté conectado.
      </Paragraph>

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space wrap>
          <Text>Servidor activado para esta organización</Text>
          <Switch
            checked={server.enabled}
            disabled={!isSuperadmin || saving}
            onChange={(enabled) =>
              void run(() => updateMcpTenantServer(tenant, serverId, { enabled }), enabled ? 'Servidor activado' : 'Servidor desactivado')
            }
          />
          {!isSuperadmin && <Text type="secondary">(solo un administrador de la plataforma puede cambiarlo)</Text>}
        </Space>

        {server.enabled && (
          <>
            <Space wrap>
              <Text>Sin regla específica, los miembros:</Text>
              <Select<McpDefaultAccess>
                value={server.defaultAccess}
                disabled={saving}
                style={{ width: 260 }}
                options={[
                  { value: 'members', label: 'tienen acceso (todos los miembros)' },
                  { value: 'none', label: 'no tienen acceso (solo con regla)' },
                ]}
                onChange={(defaultAccess) =>
                  void run(() => updateMcpTenantServer(tenant, serverId, { defaultAccess }), 'Regla por defecto actualizada')
                }
              />
            </Space>

            <Table<McpAccessRule>
              size="small"
              rowKey={(rule) => `${rule.principalType}:${rule.principal}`}
              pagination={false}
              dataSource={rules}
              locale={{ emptyText: 'Sin reglas específicas' }}
              columns={[
                {
                  title: 'Destinatario',
                  key: 'principal',
                  render: (_: unknown, rule: McpAccessRule) => (
                    <Space>
                      <Tag>{rule.principalType === 'email' ? 'persona' : 'rol'}</Tag>
                      <Text code>{rule.principal}</Text>
                    </Space>
                  ),
                },
                {
                  title: 'Acceso',
                  key: 'allowed',
                  width: 180,
                  render: (_: unknown, rule: McpAccessRule) => (
                    <Select<Verdict>
                      size="small"
                      value={rule.allowed ? 'allow' : 'deny'}
                      style={{ width: 120 }}
                      options={VERDICT_OPTIONS}
                      onChange={(verdict) =>
                        setRules(rules.map((r) => (r === rule ? { ...r, allowed: verdict === 'allow' } : r)))
                      }
                    />
                  ),
                },
                {
                  title: '',
                  key: 'remove',
                  width: 48,
                  render: (_: unknown, rule: McpAccessRule) => (
                    <Button
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      aria-label="Quitar regla"
                      onClick={() => setRules(rules.filter((r) => r !== rule))}
                    />
                  ),
                },
              ]}
            />

            <Space wrap>
              <Select<McpPrincipalType>
                value={newType}
                style={{ width: 110 }}
                options={[
                  { value: 'email', label: 'Persona' },
                  { value: 'role', label: 'Rol' },
                ]}
                onChange={(value) => {
                  setNewType(value);
                  setNewPrincipal('');
                }}
              />
              {newType === 'email' ? (
                <Input
                  placeholder="persona@empresa.com"
                  style={{ width: 240 }}
                  value={newPrincipal}
                  onChange={(e) => setNewPrincipal(e.target.value)}
                  onPressEnter={addRule}
                />
              ) : (
                <Select
                  placeholder="Rol"
                  style={{ width: 240 }}
                  value={newPrincipal || undefined}
                  options={ROLE_OPTIONS}
                  onChange={setNewPrincipal}
                />
              )}
              <Select<Verdict>
                value={newAllowed ? 'allow' : 'deny'}
                style={{ width: 110 }}
                options={VERDICT_OPTIONS}
                onChange={(verdict) => setNewAllowed(verdict === 'allow')}
              />
              <Button icon={<PlusOutlined />} onClick={addRule}>Añadir regla</Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={saving}
                disabled={!dirty}
                onClick={() => void run(async () => { await saveMcpAccessRules(tenant, serverId, rules); }, 'Reglas guardadas')}
              >
                Guardar reglas
              </Button>
            </Space>
            <Text type="secondary">
              Una regla por persona prevalece sobre una por rol, y esta sobre la regla por defecto.
              Quien no sea miembro de la organización nunca tiene acceso, aunque aparezca aquí.
            </Text>
          </>
        )}

        {isSuperadmin && (
          <div>
            <Text strong>Proveedor de identidad</Text>
            <Paragraph type="secondary" style={{ margin: '4px 0 8px' }}>
              Cómo inician sesión las personas de esta organización (hoy: Google). Solo se aceptan
              cuentas de estos dominios.
            </Paragraph>
            <Space wrap>
              <Select
                mode="tags"
                style={{ minWidth: 320 }}
                placeholder="dominio.com"
                value={domains}
                tokenSeparators={[',', ' ']}
                onChange={(values: string[]) => setDomains(values)}
              />
              <Button
                loading={saving}
                disabled={
                  domains.length === 0 ||
                  (JSON.stringify(domains) === JSON.stringify(access.identityProvider?.allowedDomains ?? []) &&
                    autoProvision === (access.identityProvider?.autoProvision ?? false))
                }
                onClick={() =>
                  void run(() => saveMcpIdentityProvider(tenant, domains, autoProvision), 'Proveedor de identidad guardado')
                }
              >
                Guardar
              </Button>
            </Space>
            <Space align="start" style={{ marginTop: 12 }}>
              <Switch checked={autoProvision} disabled={saving} onChange={setAutoProvision} />
              <div>
                <Text>Dar de alta automáticamente a quien inicie sesión con estos dominios</Text>
                <Paragraph type="secondary" style={{ margin: 0 }}>
                  La primera vez que alguien con una cuenta de estos dominios inicie sesión (en
                  CubicOS o al conectar un MCP) se crea su usuario y se le añade a esta
                  organización como <Text code>viewer</Text>. Quien ya sea miembro no cambia.
                  Quitar a alguien de la organización no es definitivo mientras esto esté activo:
                  para bloquearlo usa una regla «Denegar».
                </Paragraph>
              </div>
            </Space>
          </div>
        )}
      </Space>
    </Card>
  );
};

export default McpAccessPanel;
